import React, { useEffect, useMemo, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import {
	graphql,
	type PreloadedQuery,
	usePreloadedQuery,
	useRefetchableFragment,
} from 'react-relay';
import FilterRefinement from '@atlassian/jira-filter-refinement/src/ui/index.tsx';
import { useSuspenselessRefetch } from '@atlassian/jira-issue-hooks/src/services/use-suspenseless-refetch/index.tsx';
import type { backlogFilterRefinementQuery } from '@atlassian/jira-relay/src/__generated__/backlogFilterRefinementQuery.graphql';
import backlogFilterRefinementRefetchQuery, {
	type backlogFilterRefinementRefetchQuery as backlogFilterRefinementRefetchQueryType,
} from '@atlassian/jira-relay/src/__generated__/backlogFilterRefinementRefetchQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { backlogFilterRefinement_softwareBacklog$key } from '@atlassian/jira-relay/src/__generated__/backlogFilterRefinement_softwareBacklog.graphql';
import { useIntl } from '@atlassian/jira-intl';
import { useBacklogSelector } from '../../../../state/index.tsx';
import { getRapidViewId } from '../../../../state/selectors/app/index.tsx';
import { getOrderedAssignees } from '../../../../state/selectors/cards/index.tsx';
import { isCriticalDataLoaded } from '../../../../state/selectors/config/index.tsx';
import messages from './messages.tsx';

export const BacklogFilterRefinement = ({
	backlogFilterRefinementQueryRef,
}: {
	backlogFilterRefinementQueryRef: PreloadedQuery<backlogFilterRefinementQuery>;
}) => {
	const { formatMessage } = useIntl();
	const boardId = useBacklogSelector(getRapidViewId);
	const assignees = useBacklogSelector(getOrderedAssignees);
	const isDataLoaded = useBacklogSelector(isCriticalDataLoaded);
	const cloudId = useCloudId();

	const scope = useMemo(
		() => ({
			board: {
				boardId: Number(boardId),
			},
		}),
		[boardId],
	);

	const previousAssignees = useRef(assignees);
	const backlogFilterRefinementQueryData = usePreloadedQuery(
		graphql`
			query backlogFilterRefinementQuery($cloudId: ID!, $filterRefinementScope: JiraJqlScopeInput!)
			@preloadable {
				...backlogFilterRefinement_softwareBacklog
					@arguments(cloudId: $cloudId, scope: $filterRefinementScope)
			}
		`,
		backlogFilterRefinementQueryRef,
	);
	const [filterRefinementFragment, refetch] = useRefetchableFragment<
		backlogFilterRefinementRefetchQueryType,
		backlogFilterRefinement_softwareBacklog$key
	>(
		graphql`
			fragment backlogFilterRefinement_softwareBacklog on Query
			@argumentDefinitions(cloudId: { type: "ID!" }, scope: { type: "JiraJqlScopeInput" })
			@refetchable(queryName: "backlogFilterRefinementRefetchQuery") {
				...ui_filterRefinement_FilterRefinement @arguments(cloudId: $cloudId, scope: $scope)
			}
		`,
		backlogFilterRefinementQueryData,
	);
	const [suspenselessRefetch] = useSuspenselessRefetch(
		backlogFilterRefinementRefetchQuery,
		refetch,
		{
			fetchPolicy: 'network-only',
		},
	);

	const handleOnSearch = () => {
		// TODO EM-9640 in page filtering
	};

	useEffect(() => {
		if (isDataLoaded && !isEqual(assignees, previousAssignees.current)) {
			previousAssignees.current = assignees;
			suspenselessRefetch({ cloudId, scope });
		}
	}, [assignees, cloudId, isDataLoaded, previousAssignees, scope, suspenselessRefetch]);

	return (
		<FilterRefinement
			debounce={0}
			defaultJql=""
			onSearch={handleOnSearch}
			scope={scope}
			fragmentRef={filterRefinementFragment}
			showAssigneeFilter
			searchFieldPlaceholder={formatMessage(messages.searchPlaceholder)}
		/>
	);
};
